import React, { useState, useEffect } from 'react';
import axios from '../../services/axios';
import { useNavigate } from 'react-router-dom';
import { clearExamData } from '../../utils/helpers';

const Register = () => {
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerificationInfo, setShowVerificationInfo] = useState(false);
  const [verificationLink, setVerificationLink] = useState('');
  const navigate = useNavigate();

  // Clear exam data on mount
  useEffect(() => {
    clearExamData();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔄 [REGISTER] Submit started');
    
    setError('');
    setSuccess('');
    setShowVerificationInfo(false);
    setLoading(true);

    try {
      // Client-side validation
      if (!form.name || form.name.trim().length < 2) {
        setError('Nama minimal 2 karakter');
        setLoading(false);
        return;
      }
      
      if (!form.email || !form.email.includes('@')) {
        setError('Email tidak valid');
        setLoading(false);
        return;
      }
      
      if (!form.password || form.password.length < 6) {
        setError('Password minimal 6 karakter');
        setLoading(false);
        return;
      }

      console.log('📤 [REGISTER] Sending request for:', form.email);

      const res = await axios.post('/auth/register', form);
      
      console.log('✅ [REGISTER] Response:', {
        success: res.data.success,
        message: res.data.message,
        hasToken: !!res.data.token,
        requiresVerification: res.data.requiresVerification
      });

      // 🚨 **KEY FIX: SELALU TAMPILKAN VERIFIKASI MESSAGE**
      // JANGAN auto-login atau navigate ke dashboard
      
      if (res.data.success) {
        // TAMPILKAN VERIFIKASI MESSAGE
        setSuccess(res.data.message || 'Registrasi berhasil! Silakan cek email untuk verifikasi.');
        setShowVerificationInfo(true);
        
        // Simpan verification link untuk development
        if (res.data.verificationLink) {
          setVerificationLink(res.data.verificationLink);
          console.log('🔗 Verification Link:', res.data.verificationLink);
        }
        
        // 🚫 JANGAN LOGIN OTOMATIS
        // 🚫 JANGAN NAVIGATE KE DASHBOARD
        // Biarkan user melihat pesan verifikasi
        
      } else {
        // Jika backend return success: false
        setError(res.data.message || 'Registrasi gagal');
      }
      
    } catch (err) {
      console.error('❌ [REGISTER] Error:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      const errorMsg = err.response?.data?.message || 'Registrasi gagal. Silakan coba lagi.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleTestVerification = () => {
    if (verificationLink) {
      window.open(verificationLink, '_blank');
    }
  };

  const handleResendVerification = async () => {
    if (!form.email) {
      setError('Email tidak ditemukan');
      return;
    }
    
    try {
      setLoading(true);
      const res = await axios.post('/auth/resend-verification', {
        email: form.email.trim()
      });
      
      setSuccess('Link verifikasi baru telah dikirim!');
      
      if (res.data.verificationLink) {
        setVerificationLink(res.data.verificationLink);
        console.log('🔗 New Verification Link:', res.data.verificationLink);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Gagal mengirim ulang verifikasi';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForm = () => {
    setShowVerificationInfo(false);
    setForm({ name: '', email: '', password: '' });
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logoContainer}>
            <img 
              src="/img/proest.png" 
              alt="PRO-EST Logo" 
              style={styles.logo}
              onError={(e) => {
                console.error('❌ Logo not found');
                e.target.style.display = 'none';
                
                const placeholder = document.createElement('div');
                placeholder.style.cssText = `
                  width: 70px;
                  height: 70px;
                  background: linear-gradient(135deg, #1a3c6d, #4a90e2);
                  border-radius: 12px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: white;
                  font-weight: bold;
                  font-size: 28px;
                  box-shadow: 0 4px 12px rgba(26, 60, 109, 0.3);
                `;
                placeholder.textContent = 'P';
                
                const parent = e.target.parentNode;
                parent.innerHTML = '';
                parent.appendChild(placeholder);
              }}
            />
          </div>
          <div style={styles.titleContainer}>
            <h2 style={styles.title}>PRO-EST</h2>
            <p style={styles.subtitle}>Create New Account</p>
          </div>
        </div>

        {error && (
          <div style={styles.alertError}>
            <div style={styles.alertIcon}>⚠️</div>
            <div style={styles.alertText}>{error}</div>
          </div>
        )}

        {success && (
          <div style={styles.alertSuccess}>
            <div style={styles.alertIcon}>✅</div>
            <div style={styles.alertText}>{success}</div>
          </div>
        )}

        {!showVerificationInfo ? (
          // FORM REGISTRASI
          <form onSubmit={handleSubmit}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Full Name</label>
              <input
                type="text"
                name="name"
                placeholder="Enter your full name"
                value={form.name}
                onChange={handleChange}
                style={styles.input}
                required
                minLength="2"
                disabled={loading}
                autoComplete="name"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={form.email}
                onChange={handleChange}
                style={styles.input}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                name="password"
                placeholder="Minimum 6 characters"
                value={form.password}
                onChange={handleChange}
                style={styles.input}
                minLength="6"
                required
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              style={{ ...styles.button, opacity: loading ? 0.6 : 1 }}
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
        ) : (
          // VERIFICATION INFO SCREEN
          <div style={styles.verificationScreen}>
            <div style={styles.verificationIcon}>📧</div>
            <h3 style={styles.verificationTitle}>Check Your Email</h3>
            
            <div style={styles.verificationContent}>
              <p style={styles.verificationText}>
                We've sent a verification link to:
              </p>
              <p style={styles.verificationEmail}>{form.email}</p>
              <p style={styles.verificationInstructions}>
                Please check your <strong>inbox</strong> or <strong>spam folder</strong> and click the link to verify your account.
              </p>
            </div>

            {/* DEVELOPMENT MODE */}
            {process.env.NODE_ENV !== 'production' && verificationLink && (
              <div style={styles.devSection}>
                <p style={styles.devTitle}>🛠️ Development Mode</p>
                <button 
                  onClick={handleTestVerification}
                  style={styles.devButton}
                >
                  Test Verification Link
                </button>
                <p style={styles.devLink}>
                  {verificationLink.substring(0, 60)}...
                </p>
              </div>
            )}

            <div style={styles.verificationActions}>
              <button 
                onClick={handleResendVerification}
                style={styles.secondaryButton}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Resend Verification Email'}
              </button>
              
              <button 
                onClick={handleBackToForm}
                style={styles.textButton}
              >
                ← Register Different Email
              </button>
            </div>
          </div>
        )}

        <div style={styles.footer}>
          <p style={styles.footerText}>
            Already have an account?{' '}
            <a href="/" style={styles.footerLink}>
              Login here
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a3c6d, #4a90e2)',
    fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif',
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '500px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #1a3c6d, #2a5caa)',
    padding: '30px',
    textAlign: 'center',
    color: 'white',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '15px',
  },
  logo: {
    width: '80px',
    height: '80px',
    objectFit: 'contain',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    border: '3px solid rgba(255, 255, 255, 0.3)',
  },
  titleContainer: {
    textAlign: 'center',
  },
  title: {
    margin: 0,
    fontSize: '32px',
    fontWeight: '800',
    letterSpacing: '0.5px',
  },
  subtitle: {
    margin: '5px 0 0 0',
    fontSize: '14px',
    opacity: 0.9,
    fontWeight: '500',
  },
  alertError: {
    display: 'flex',
    alignItems: 'center',
    background: '#fef2f2',
    borderLeft: '4px solid #ef4444',
    padding: '12px 16px',
    margin: '20px',
    borderRadius: '8px',
  },
  alertSuccess: {
    display: 'flex',
    alignItems: 'center',
    background: '#f0fdf4',
    borderLeft: '4px solid #22c55e',
    padding: '12px 16px',
    margin: '20px',
    borderRadius: '8px',
  },
  alertIcon: {
    fontSize: '18px',
    marginRight: '12px',
  },
  alertText: {
    flex: 1,
    fontSize: '14px',
    fontWeight: '500',
  },
  inputGroup: {
    padding: '0 30px',
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '16px',
    boxSizing: 'border-box',
    transition: 'all 0.2s',
    outline: 'none',
    ':focus': {
      borderColor: '#1a3c6d',
      boxShadow: '0 0 0 3px rgba(26, 60, 109, 0.1)',
    },
  },
  button: {
    width: 'calc(100% - 60px)',
    margin: '0 30px 30px',
    padding: '16px',
    background: 'linear-gradient(135deg, #1a3c6d, #2a5caa)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
    boxShadow: '0 4px 12px rgba(26, 60, 109, 0.3)',
    ':hover': {
      background: 'linear-gradient(135deg, #15325a, #1a3c6d)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(26, 60, 109, 0.4)',
    },
    ':active': {
      transform: 'translateY(0)',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
      transform: 'none',
    },
  },
  verificationScreen: {
    padding: '40px 30px',
    textAlign: 'center',
  },
  verificationIcon: {
    fontSize: '60px',
    marginBottom: '20px',
    animation: 'bounce 2s infinite',
  },
  verificationTitle: {
    margin: '0 0 16px 0',
    fontSize: '24px',
    color: '#1a3c6d',
    fontWeight: 'bold',
  },
  verificationContent: {
    background: '#f8fafc',
    padding: '24px',
    borderRadius: '12px',
    marginBottom: '24px',
    border: '1px solid #e2e8f0',
  },
  verificationText: {
    margin: '0 0 8px 0',
    color: '#64748b',
    fontSize: '15px',
  },
  verificationEmail: {
    margin: '0 0 12px 0',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1a3c6d',
    wordBreak: 'break-all',
  },
  verificationInstructions: {
    margin: '16px 0 0 0',
    color: '#64748b',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  devSection: {
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '24px',
  },
  devTitle: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#9a3412',
  },
  devButton: {
    width: '100%',
    padding: '12px',
    background: '#ea580c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginBottom: '8px',
    transition: 'background 0.2s',
    ':hover': {
      background: '#c2410c',
    },
  },
  devLink: {
    margin: '8px 0 0 0',
    fontSize: '11px',
    color: '#9a3412',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
    lineHeight: '1.4',
  },
  verificationActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  secondaryButton: {
    padding: '14px',
    background: '#4a90e2',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      background: '#357ae8',
      transform: 'translateY(-1px)',
    },
  },
  textButton: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: '14px',
    textDecoration: 'underline',
    padding: '8px',
    ':hover': {
      color: '#1a3c6d',
    },
  },
  footer: {
    padding: '20px 30px',
    borderTop: '1px solid #e5e7eb',
    textAlign: 'center',
  },
  footerText: {
    margin: 0,
    fontSize: '14px',
    color: '#6b7280',
  },
  footerLink: {
    color: '#1a3c6d',
    textDecoration: 'none',
    fontWeight: 'bold',
    ':hover': {
      textDecoration: 'underline',
    },
  },
};

// Add CSS animations
const addStyles = () => {
  if (typeof window === 'undefined') return;
  
  const style = document.createElement('style');
  style.textContent = `
    @keyframes bounce {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-10px);
      }
    }
    
    input:focus {
      border-color: #1a3c6d !important;
      box-shadow: 0 0 0 3px rgba(26, 60, 109, 0.1) !important;
      outline: none;
    }
    
    button:disabled {
      opacity: 0.6 !important;
      cursor: not-allowed !important;
      transform: none !important;
    }
    
    a:hover {
      text-decoration: underline !important;
    }
  `;
  document.head.appendChild(style);
};

// Add styles on component mount
if (typeof window !== 'undefined') {
  addStyles();
}

export default Register;
